import React, { useState } from 'react';
import { BuilderData, BuilderToCompareData, Comparitor, StateToShow, UserDefinedDataset } from '../types';
import { parameterPrettyText } from '../functions/miscellaneous';

interface CriteriaDataEditorProps {
    data: BuilderData;
    onUpdate: (data: BuilderData) => void;
    updateSelectors: (criteria_data: BuilderToCompareData) => Promise<void>,
    current_selected_index: number | null, 
    setCurrentSelectedIndex: React.Dispatch<React.SetStateAction<number | null>>,
    handleCriteriaChange: (dataset_name: string, attribute_name: string, year: number | undefined, month: number | undefined, current_parameters: StateToShow) => Promise<void>,
    setCurrentComparison: React.Dispatch<React.SetStateAction<Comparitor | null>>,
    setInputValues: React.Dispatch<React.SetStateAction<number | (number | undefined)[] | string[] | undefined>>,
    updateToDefault: () => void,
    user_data: UserDefinedDataset, 
    setShowUserDataset: (user_data: UserDefinedDataset, current_user_data: string | undefined) => void,
    setValidCalculate: React.Dispatch<React.SetStateAction<boolean>>
}

const CriteriaDataEditor: React.FC<CriteriaDataEditorProps> = ({ 
    data, 
    onUpdate, 
    updateSelectors,
    current_selected_index,
    setCurrentSelectedIndex,
    handleCriteriaChange,
    setCurrentComparison,
    setInputValues,
    updateToDefault,
    user_data,
    setShowUserDataset,
    setValidCalculate
}) => {
    // State to track which items have expanded details
    const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

    const handleDelete = (index: number) => {
        setValidCalculate(false);
        const newData = data.filter((_, i) => i !== index);
        if (current_selected_index && index < current_selected_index){
            if(current_selected_index-1 >= 0) {
                setCurrentSelectedIndex(current_selected_index-1);
            }
        } else if(current_selected_index && index == current_selected_index){
            var new_index: number | undefined = -99;
            if(current_selected_index >= newData.length){
                new_index = current_selected_index - 1;
            } else {
                new_index = current_selected_index;
            }

            if(new_index < 0){
                updateToDefault();
                setCurrentComparison(null);
                setInputValues(undefined);
            } else {
                const tmp_left = newData[new_index].left
                if(tmp_left) updateSelectors(tmp_left);
                else { updateToDefault(); } 

                const tmp_comp = newData[new_index].compare;
                const tmp_right = newData[new_index].right;
                
                setCurrentComparison(tmp_comp);
                setInputValues(tmp_right);
            }
        }
        onUpdate(newData);
    };

    const formatRightValue = (right: (number | undefined)[] | number | string[] | undefined): string => {
        if (!right) return '';
        if (Array.isArray(right)) {
            return right.map(val => val ?? '').join(', ');
        }
        return right.toString();
    };

    const formatLeftValue = (left: BuilderToCompareData | null) => {
        if(left == null) return "";
        return left.attribute_title;
    };

    const onEditClick = (index: number) => {
        setCurrentSelectedIndex(current_selected_index === index ? null : index)
        if(data[index].left) {
            if(data[index].left.is_user_defined){
                setShowUserDataset(user_data, data[index].left.dataset)
            } else {
                const tmp_left = data[index].left;
                handleCriteriaChange(tmp_left.dataset, tmp_left.attribute_name, tmp_left.year, tmp_left.month, tmp_left.parameter)
            }
        } else {
            updateToDefault();
        }
        const tmp_comp = data[index].compare;
        const tmp_right = data[index].right;
        
        setCurrentComparison(tmp_comp);
        setInputValues(tmp_right);
    };

    const capitalizeFirstLetter = (str: string | null) => {
        if (!str) return null;
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    // Toggle details visibility
    const toggleDetails = (index: number) => {
        setExpandedItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    return (
        <div className="p-4">
            {data.map((line, index) => (
                <div key={index} 
                    style={{
                        border: '1px solid #4a6fa5',
                        borderLeft: current_selected_index === index ? '5px solid #1976d2' : '1px solid #4a6fa5',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '12px',
                        //backgroundColor: current_selected_index === index ? '#f7f9ff' : 'white',
                        backgroundColor: 'white',
                        position: 'relative',
                        minHeight: '100px',
                    }}
                >
                    <div style={{ 
                        fontSize: '15px', 
                        fontWeight: 500, 
                        marginBottom: '12px',
                        color: '#1976d2'
                    }}>
                        Criteria #{(data.length - index)}
                    </div>
                    
                    <div style={{ 
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '12px',
                        width: '100%'
                        
                    }}>
                        { line.left && <div style={{
                            padding: '6px 12px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '16px',
                            border: '1px solid #e0e0e0',
                            color: '#1976d2',
                            fontWeight: 500,
                            fontSize: '14px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                            minHeight: '32px', // Add minimum height
                            minWidth: '40px',  // Add minimum width
                        }}>
                            {formatLeftValue(line.left)}
                        </div> }
                        
                        {line.compare && <div style={{
                            padding: '6px 12px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '16px',
                            border: '1px solid #e0e0e0',
                            color: '#1976d2',
                            fontWeight: 500,
                            fontSize: '14px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',

                            minHeight: '32px', // Add minimum height
                            minWidth: '40px',  // Add minimum width
                        }}>
                            {capitalizeFirstLetter(line.compare) || ""}
                        </div>}
                        
                        {line.right && <div style={{
                            padding: '6px 12px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '16px',
                            border: '1px solid #e0e0e0',
                            color: '#1976d2',
                            fontWeight: 500,
                            fontSize: '14px',
                            whiteSpace: 'nowrap',  // Keep on one line
                            overflow: 'hidden',    // Hide overflow
                            textOverflow: 'ellipsis', // Show ellipsis
                            maxWidth: '100%',      // Take full available width
                            display: 'inline-block', // Necessary for text-overflow to work

                            minHeight: '32px', // Add minimum height
                            minWidth: '40px',  // Add minimum width
                        }}>
                            {formatRightValue(line.right)}
                        </div>}
                    </div>
                    
                    {/* Action buttons */}
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end',
                        gap: '12px'
                    }}>
                        <button
                            onClick={() => toggleDetails(index)}
                            style={{ 
                                backgroundColor: 'transparent',
                                border: 'none',
                                color: '#2196F3',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                fontSize: '14px'
                            }}
                        >
                            {expandedItems.has(index) ? 'Hide Details' : 'View Details'}
                        </button>
                        
                        <button
                            onClick={() => onEditClick(index)}
                            style={{ 
                                backgroundColor: 'transparent',
                                border: 'none',
                                color: '#2196F3',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                fontSize: '14px'
                            }}
                        >
                            Edit
                        </button>
                        
                        <button
                            onClick={() => handleDelete(index)}
                            style={{ 
                                backgroundColor: 'transparent',
                                border: 'none',
                                color: '#2196F3',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                fontSize: '14px'
                            }}
                        >
                            Delete
                        </button>
                    </div>
                    
                    {/* Details section - conditionally visible */}
                    {expandedItems.has(index) && line.left && (
                        <div style={{
                            padding: '16px 0', // Remove left padding to align with header
                            marginTop: '12px',
                            backgroundColor: 'transparent', // Remove background color
                            borderTop: '1px solid #e9ecef', // Keep a subtle separator
                            borderBottom: 'none',
                            borderLeft: 'none',
                            borderRight: 'none',
                            borderRadius: '0',
                            fontSize: '14px',
                            color: '#5c6bc0' // Blue-ish text color
                        }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                <strong> Dataset: </strong> {line.left?.dataset_title}
                            </div>
                            
                            {!line.left?.is_user_defined && line.left?.attribute_name && (
                                <div style={{ marginBottom: '4px' }}>                                                                          
                                   <strong> Attribute: </strong> {line.left?.attribute_title}
                                </div>
                            )}

                            {(line.left?.year ? !isNaN(line.left?.year) : false) && !line.left?.is_user_defined && line.left?.year &&(
                                <div style={{ marginBottom: '4px' }}>
                                    <strong>Year:</strong> { !isNaN(line.left?.year) ? line.left?.year : ""}
                                </div>
                            )}
                            
                            {!line.left?.is_user_defined && line.left?.parameter && Object.keys(line.left?.parameter).length !== 0 && (
                                <div style={{ marginBottom: '4px' }}>
                                    <strong> Parameter(s): </strong> {parameterPrettyText(line.left?.parameter) || ""}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default CriteriaDataEditor;